import { useAuthInfo } from "@propelauth/react";
import * as Sentry from "@sentry/react";
import { PropsWithChildren, useEffect } from "react";
import { toast } from "sonner";

const NETWORK_STATUS_TOAST_ID = "network-status";

const isPropelAuthRefreshNetworkError = (event: Sentry.Event) => {
  const exceptions = event.exception?.values ?? [];

  return exceptions.some((exception) => {
    const isFailedFetch =
      exception.type === "TypeError" && exception.value === "Failed to fetch";
    const frames = exception.stacktrace?.frames ?? [];
    const isPropelAuthRefresh = frames.some((frame) => {
      return (
        frame.filename?.includes("@propelauth/react") &&
        (frame.function === "fetchAuthenticationInfo" ||
          frame.function === "forceRefreshToken")
      );
    });

    return isFailedFetch && isPropelAuthRefresh;
  });
};

export const ServicesProviderWrapper = ({ children }: PropsWithChildren) => {
  const authInfo = useAuthInfo();

  const initSentry = () => {
    const sentryDsn = import.meta.env.VITE_SENTRY_DSN;
    if (sentryDsn) {
      Sentry.init({
        dsn: sentryDsn,
        integrations: [
          Sentry.browserTracingIntegration(),
          Sentry.replayIntegration(),
        ],
        // Performance Monitoring
        tracesSampleRate: 0.1, //  Capture 100% of the transactions
        // Set 'tracePropagationTargets' to control for which URLs distributed tracing should be enabled
        tracePropagationTargets: ["api.mdley.co"],
        beforeSend(event) {
          if (isPropelAuthRefreshNetworkError(event)) {
            return null;
          }

          return event;
        },
        replaysSessionSampleRate: authInfo.user?.email.endsWith(
          "ihsaan@mdley.co"
        )
          ? 0.0
          : 1.0,
      });
    }
  };

  useEffect(() => {
    if (authInfo.user?.email) {
      initSentry();
    }
  }, [authInfo.user?.email]);

  useEffect(() => {
    const showOfflineToast = () => {
      toast.error("Connection lost. Check your internet connection.", {
        id: NETWORK_STATUS_TOAST_ID,
        duration: Number.POSITIVE_INFINITY,
      });
    };

    const showOnlineToast = () => {
      toast.success("Connection restored.", {
        id: NETWORK_STATUS_TOAST_ID,
        duration: 4000,
      });
    };

    window.addEventListener("offline", showOfflineToast);
    window.addEventListener("online", showOnlineToast);

    if (!navigator.onLine) {
      showOfflineToast();
    }

    return () => {
      window.removeEventListener("offline", showOfflineToast);
      window.removeEventListener("online", showOnlineToast);
    };
  }, []);

  return <>{children}</>;
};
